import { GameInformationTypeEnum } from "entities/game/types";
import { useMetadataTagsListQuery } from "entities/metadataTags/queries/useMetadataTagsListQuery";
import { MetadataTagPrimaryType } from "entities/tagTypes/types";
import { useCallback, useMemo, useState } from "react";
import { ComboGroupedElement } from "shared/components/Combobox/type";
import { getFilteredTagsBySearchValue } from "../utils";

export type MetadataTagsFilterApi = {
  selectedMetadataTags: string[];
  handleSelectGroupedOptions: (values: string[]) => void;
  handleClearSelectedOptions: () => void;
  groupedMetadataTags: ComboGroupedElement[];
  metadataTagsFilterGames: string;
  metadataTagsSearchQuery: string;
  handleSearchMetadataTags: (query: string) => void;
};

export const useMetadataTagsFilter = (): MetadataTagsFilterApi => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedMetadataTagValues, setSelectedMetadataTagValues] = useState<
    string[]
  >([]);
  const [searchedTags, setSearchedTags] = useState<ComboGroupedElement[]>([]);

  const { data: metadataTags = [] } = useMetadataTagsListQuery();

  const metadataTagsByPrimaryTag = useMemo(() => {
    const mergedTags = metadataTags.filter(
      (tag) => tag.type === MetadataTagPrimaryType.DROPDOWN,
    );

    const tags: Map<string, { primaryTag: string; secondaryTags: string[] }> =
      new Map(
        mergedTags.map((tag) => [
          tag.id,
          {
            primaryTag: tag.primaryTag,
            secondaryTags: tag.secondaryTags || [],
          },
        ]),
      );

    return tags;
  }, [metadataTags]);

  const groupedMetadataTags: ComboGroupedElement[] = useMemo(() => {
    return Array.from(metadataTagsByPrimaryTag.entries()).map(([id, tags]) => ({
      value: id,
      name: tags.primaryTag,
      options: tags.secondaryTags.map((tag) => ({
        value: tag,
        name: tag,
      })),
    }));
  }, [metadataTagsByPrimaryTag]);

  const metadataTagsFilterGames = useMemo(() => {
    let metadataTagsFilters = [];

    const allGameFeatures = metadataTags?.find(
      (tag) => tag.primaryTag === GameInformationTypeEnum.GAME_FEATURES,
    )?.secondaryTags;

    const featuresToSearch = allGameFeatures?.filter((tag: string) =>
      selectedMetadataTagValues.includes(tag),
    );

    if (featuresToSearch?.length) {
      metadataTagsFilters.push(
        `(metadata.features:${featuresToSearch.map((value) => `"${value}"`).join(` OR metadata.features:`)})`,
      );
    }

    const gameInformation = metadataTags
      ?.filter(
        (tag) => tag.primaryTag !== GameInformationTypeEnum.GAME_FEATURES,
      )
      ?.flatMap((tag) => tag.secondaryTags);

    const gameInformationToSearch = gameInformation?.filter(
      (tag: string | undefined) =>
        selectedMetadataTagValues.includes(tag || ""),
    );

    if (gameInformationToSearch?.length) {
      metadataTagsFilters.push(
        `(metadata.gameInformation.value:${gameInformationToSearch.map((value) => `"${value}"`).join(" OR metadata.gameInformation.value:")})`,
      );
    }

    return metadataTagsFilters.join(" OR ");
  }, [metadataTags, selectedMetadataTagValues]);

  const handleSelectGroupedOptions = (values: string[]) => {
    setSelectedMetadataTagValues(values);
  };

  const handleSearchMetadataTags = useCallback(
    (value: string) => {
      setSearchValue(value);

      const filteredTagsBySearchValue: ComboGroupedElement[] =
        getFilteredTagsBySearchValue(groupedMetadataTags, value);

      setSearchedTags(filteredTagsBySearchValue);
    },
    [groupedMetadataTags],
  );

  const handleClearSelectedOptions = () => {
    setSelectedMetadataTagValues([]);
    setSearchValue("");
    setSearchedTags([]);
  };

  return {
    selectedMetadataTags: selectedMetadataTagValues,
    handleSelectGroupedOptions,
    handleClearSelectedOptions,
    groupedMetadataTags: searchValue ? searchedTags : groupedMetadataTags,
    metadataTagsFilterGames,
    metadataTagsSearchQuery: searchValue,
    handleSearchMetadataTags,
  };
};
